import { useEffect } from 'react';
import { CallMedia, formatCallDuration } from '@forever/shared';
import type { CallState } from './useBrowserCall';

/**
 * Görüşme ekranı — panelin üstüne binen tam ekran katman.
 *
 * Tek bir bileşen hem gelen aramayı, hem giden aramayı, hem de kurulmuş
 * görüşmeyi çiziyor. Ayrı ayrı yazmak üç yerde aynı düzeni üretmek demekti;
 * aradaki fark yalnızca hangi düğmelerin göründüğü.
 *
 * Görüntülü görüşmede uzak akış tam ekranı kaplar, yerel görüntü sağ altta
 * küçük bir kutuda durur — insanların telefonlarından tanıdığı yerleşim.
 */

export interface CallOverlayProps {
  state: CallState;
  localVideoRef: React.Ref<HTMLVideoElement>;
  remoteVideoRef: React.Ref<HTMLVideoElement>;
  remoteAudioRef: React.Ref<HTMLAudioElement>;
  /** Gelen arama ekranındayken — yoksa giden/aktif görüşme. */
  incoming?: boolean;
  onAccept?: () => void;
  onDecline?: () => void;
  onHangUp: () => void;
  onToggleMic: () => void;
  onToggleCamera: () => void;
  onClose: () => void;
}

const PHASE_TEXT: Record<CallState['phase'], string> = {
  idle: '',
  'requesting-permission': 'İzin bekleniyor…',
  calling: 'Aranıyor…',
  ringing: 'Çalıyor…',
  connecting: 'Bağlanıyor…',
  active: '',
  ended: 'Görüşme bitti',
  failed: 'Görüşme kurulamadı',
};

export function CallOverlay({
  state,
  localVideoRef,
  remoteVideoRef,
  remoteAudioRef,
  incoming = false,
  onAccept,
  onDecline,
  onHangUp,
  onToggleMic,
  onToggleCamera,
  onClose,
}: CallOverlayProps) {
  const { phase, media, peerName, durationSeconds, errorText, remoteVideoOn, micOn, cameraOn } =
    state;

  const video = media === CallMedia.VIDEO;
  const closed = phase === 'ended' || phase === 'failed';

  /* Escape yalnızca görüşme bittikten sonra kapatır: aktif görüşmede
     yanlışlıkla basmak aramayı düşürmemeli. */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && closed) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [closed, onClose]);

  if (phase === 'idle') return null;

  return (
    <div className="fd-call" role="dialog" aria-modal="true" aria-label="Görüşme">
      {/* Ses her durumda çalar; görüntü olmasa bile bu eleman gerekli. */}
      <audio ref={remoteAudioRef} autoPlay playsInline />

      <div className="fd-call__stage" data-video={video && remoteVideoOn}>
        {video && remoteVideoOn ? (
          <video ref={remoteVideoRef} className="fd-call__remote" autoPlay playsInline />
        ) : (
          <div className="fd-call__avatar" aria-hidden="true">
            {(peerName ?? '?').slice(0, 1).toUpperCase()}
          </div>
        )}

        {video && (
          <video ref={localVideoRef} className="fd-call__local" autoPlay playsInline muted />
        )}
      </div>

      <div className="fd-call__info">
        <p className="fd-call__name">{peerName ?? 'Görüşme'}</p>
        <p className="fd-call__status">
          {phase === 'active'
            ? formatCallDuration(durationSeconds)
            : incoming && phase !== 'connecting'
              ? `Gelen ${video ? 'görüntülü' : 'sesli'} arama`
              : PHASE_TEXT[phase]}
        </p>
        {errorText && <p className="fd-call__error">{errorText}</p>}
      </div>

      <div className="fd-call__actions">
        {closed ? (
          <button type="button" className="fd-btn fd-btn--ghost" onClick={onClose}>
            Kapat
          </button>
        ) : incoming && phase !== 'connecting' && phase !== 'active' ? (
          <>
            <button
              type="button"
              className="fd-call__btn fd-call__btn--decline"
              onClick={onDecline}
              aria-label="Reddet"
            >
              <PhoneDownIcon />
            </button>
            <button
              type="button"
              className="fd-call__btn fd-call__btn--accept"
              onClick={onAccept}
              aria-label="Cevapla"
            >
              <PhoneUpIcon />
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              className="fd-call__btn"
              data-off={!micOn}
              onClick={onToggleMic}
              aria-label={micOn ? 'Mikrofonu kapat' : 'Mikrofonu aç'}
              aria-pressed={!micOn}
            >
              <MicIcon off={!micOn} />
            </button>

            {video && (
              <button
                type="button"
                className="fd-call__btn"
                data-off={!cameraOn}
                onClick={onToggleCamera}
                aria-label={cameraOn ? 'Kamerayı kapat' : 'Kamerayı aç'}
                aria-pressed={!cameraOn}
              >
                <CameraIcon off={!cameraOn} />
              </button>
            )}

            <button
              type="button"
              className="fd-call__btn fd-call__btn--decline"
              onClick={onHangUp}
              aria-label="Görüşmeyi bitir"
            >
              <PhoneDownIcon />
            </button>
          </>
        )}
      </div>
    </div>
  );
}

/* --------------------------------- simgeler -------------------------------- */

const STROKE = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

function PhoneUpIcon() {
  return (
    <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">
      <path
        {...STROKE}
        d="M6.6 4.5h2.2l1.4 3.6-1.8 1.3a11.5 11.5 0 0 0 5.2 5.2l1.3-1.8 3.6 1.4v2.2a2 2 0 0 1-2.2 2A15.6 15.6 0 0 1 4.6 6.7a2 2 0 0 1 2-2.2Z"
      />
    </svg>
  );
}

function PhoneDownIcon() {
  return (
    <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">
      <g transform="rotate(135 12 12)">
        <path
          {...STROKE}
          d="M6.6 4.5h2.2l1.4 3.6-1.8 1.3a11.5 11.5 0 0 0 5.2 5.2l1.3-1.8 3.6 1.4v2.2a2 2 0 0 1-2.2 2A15.6 15.6 0 0 1 4.6 6.7a2 2 0 0 1 2-2.2Z"
        />
      </g>
    </svg>
  );
}

function MicIcon({ off }: { off: boolean }) {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
      <rect {...STROKE} x="9" y="3" width="6" height="10" rx="3" />
      <path {...STROKE} d="M5.5 11a6.5 6.5 0 0 0 13 0M12 17.5V21" />
      {off && <path {...STROKE} d="M4 4l16 16" />}
    </svg>
  );
}

function CameraIcon({ off }: { off: boolean }) {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
      <rect {...STROKE} x="3" y="6.5" width="12" height="11" rx="2" />
      <path {...STROKE} d="M15 10.5 21 7.5v9l-6-3z" />
      {off && <path {...STROKE} d="M4 4l16 16" />}
    </svg>
  );
}
